import { useEffect, useState } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { Loader2, PartyPopper, Laptop, Tv, Smartphone, Refrigerator, WashingMachine, AirVent, Watch, Camera, Headphones, Package, Microwave, Car, Bike, Sofa } from "lucide-react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useApp } from "@/lib/app-context";
import { useApi } from "@/lib/api";
import { COUNTRIES } from "@/lib/geo";
import { toast } from "sonner";
import { reload, sendEmailVerification } from "firebase/auth";
import { auth, authError } from "@/lib/firebase";

export const Spinner = ({ className = "" }) => (
  <div className={`flex items-center justify-center py-24 ${className}`} data-testid="loading-spinner"><Loader2 className="h-7 w-7 animate-spin text-ww-emerald" /></div>
);

export function RequireAuth({ children }) {
  const { isLoaded, isSignedIn, needsEmailVerify } = useAuth();
  const { me } = useApp();
  if (!isLoaded) return <Spinner />;
  if (!isSignedIn) return <Navigate to="/?signup=1" replace />;
  if (needsEmailVerify) return <VerifyEmail />;
  if (!me) return <Spinner />;
  return children;
}

function VerifyEmail() {
  const { user, signOut } = useAuth();
  const [busy, setBusy] = useState(false);
  const [wait, setWait] = useState(60);
  useEffect(() => {
    if (wait <= 0) return undefined;
    const t = setTimeout(() => setWait(wait - 1), 1000);
    return () => clearTimeout(t);
  }, [wait]);
  const act = async (fn) => { setBusy(true); try { await fn(); } catch (e) { toast.error(authError(e)); } finally { setBusy(false); } };
  const check = () => act(async () => { await reload(user); await user.getIdToken(true); if (!auth.currentUser.emailVerified) toast.info("Not verified yet. Please click the link in the email."); else window.location.reload(); });
  return (
    <main className="mx-auto max-w-lg px-5 py-20 text-center" data-testid="verify-email-page">
      <h1 className="font-display text-3xl font-semibold text-ww-navy">Verify Your Email</h1>
      <p className="mt-3 text-ww-muted">We sent a verification link to <strong className="text-ww-navy">{user.email}</strong>. Open it, then come back here.</p>
      <div className="mt-6 flex flex-wrap justify-center gap-3">
        <button className="ww-btn-primary" disabled={busy} onClick={check} data-testid="verify-email-check-button">I've Verified My Email</button>
        <button className="ww-btn-ghost" disabled={busy || wait > 0} onClick={() => act(async () => { await sendEmailVerification(user, { url: `${window.location.origin}/wallet` }); setWait(60); toast.success("Verification email sent"); })} data-testid="verify-email-resend-button">{wait > 0 ? `Resend Email in ${wait}s` : "Resend Email"}</button>
        <button className="ww-btn-ghost" onClick={() => signOut({ redirectUrl: "/" })} data-testid="verify-email-signout-button">Sign Out</button>
      </div>
    </main>
  );
}

const STATUS = {
  active: ["Active", "bg-ww-mint text-ww-emerald border-[#A7F3D0]"],
  expiring: ["Expiring Soon", "bg-amber-50 text-amber-700 border-amber-200"],
  expired: ["Expired", "bg-red-50 text-red-600 border-red-200"],
};
export const StatusBadge = ({ status, testid }) => {
  const [l, c] = STATUS[status] || STATUS.active;
  return <span data-testid={testid} className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-semibold ${c}`}><span className="h-1.5 w-1.5 rounded-full bg-current" />{l}</span>;
};

const ICONS = [[/fridge|refrigerator/i, Refrigerator], [/laptop|macbook|notebook|computer|pc/i, Laptop], [/tv|television/i, Tv],
  [/phone|iphone|mobile|galaxy/i, Smartphone], [/washing|washer|dryer/i, WashingMachine], [/\bac\b|air ?con|cooler|purifier/i, AirVent],
  [/watch/i, Watch], [/camera/i, Camera], [/headphone|earbud|speaker|audio/i, Headphones], [/microwave|oven/i, Microwave],
  [/car|scooter/i, Car], [/bike|cycle/i, Bike], [/sofa|chair|furniture|bed/i, Sofa]];
export const ProductIcon = ({ name, className = "h-6 w-6" }) => {
  const Icon = ICONS.find(([re]) => re.test(name || ""))?.[1] || Package;
  return <Icon className={className} />;
};

export function CountrySelect({ value, onChange, testid = "country-select", className = "" }) {
  return (
    <Select value={value || undefined} onValueChange={onChange}>
      <SelectTrigger className={`h-11 rounded-xl border-ww-border bg-white ${className}`} data-testid={testid}><SelectValue placeholder="Select country" /></SelectTrigger>
      <SelectContent className="max-h-72">
        {COUNTRIES.map(([cc, n]) => <SelectItem key={cc} value={cc} data-testid={`${testid}-option-${cc}`}>{n}</SelectItem>)}
      </SelectContent>
    </Select>
  );
}

export function WelcomeDialog() {
  const { me, setMe } = useApp();
  const api = useApi();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  const { needsEmailVerify } = useAuth();
  useEffect(() => { if (me && !me.welcome_seen && !needsEmailVerify && !pathname.startsWith("/family/join")) setOpen(true); }, [me, pathname, needsEmailVerify]);
  const close = async (path) => {
    setOpen(false);
    setMe((m) => m && { ...m, welcome_seen: true });
    api("post", "/me/welcome-seen").catch(() => {});
    if (path) navigate(path);
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && close()}>
      <DialogContent className="max-w-md rounded-[28px] border-ww-border p-8 text-center" data-testid="welcome-dialog">
        <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl bg-ww-mint text-ww-emerald"><PartyPopper className="h-8 w-8" /></div>
        <DialogTitle className="mt-4 font-display text-2xl font-semibold text-ww-navy">Welcome to Warranty Wallet!</DialogTitle>
        <DialogDescription className="text-ww-muted">Your digital wallet is ready. Let's save your first product.</DialogDescription>
        <div className="mt-6 grid gap-3">
          <button className="ww-btn-primary justify-center" onClick={() => close("/add")} data-testid="welcome-add-first-product">Add Your First Product</button>
          <button className="ww-btn-ghost justify-center" onClick={() => close("/wallet")} data-testid="welcome-go-to-wallet">Go to My Wallet</button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function CookieBanner() {
  const [show, setShow] = useState(!localStorage.getItem("ww_cookie_ack"));
  if (!show) return null;
  return (
    <div className="fixed inset-x-3 bottom-3 z-40 mx-auto max-w-2xl rounded-2xl border border-ww-border bg-white/95 p-4 shadow-xl backdrop-blur-xl sm:flex sm:items-center sm:gap-4" data-testid="cookie-banner">
      <p className="text-sm text-ww-muted">We use only essential cookies and local storage needed for sign-in, security and your country preference. We do not use advertising cookies. <a href="/privacy" className="font-semibold text-ww-emerald underline">Privacy Policy</a></p>
      <button className="ww-btn-primary mt-3 shrink-0 sm:mt-0" onClick={() => { localStorage.setItem("ww_cookie_ack", "1"); setShow(false); }} data-testid="cookie-accept-button">Got it</button>
    </div>
  );
}
